import React, { useState, useEffect } from 'react';
import {
    Button,
    Input,
    Switch,
    message,
    Dropdown,
    Space,
    Tooltip,
    Popover,
    Menu,
    Modal,
    Typography,
    Spin
} from 'antd';
import {
    SettingOutlined,
    DownOutlined,
    SaveOutlined,
    FileOutlined,
    ImportOutlined,
    ExportOutlined,
    PlusOutlined,
    DeleteOutlined,
    LoadingOutlined,
    StarOutlined,
    StarFilled
} from '@ant-design/icons';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import { useSharedData } from '../contexts/SharedDataContext';
import { analyticsDefaults } from './analyticsDefaults';

const { Text, Title } = Typography;

const ImprovedConfigurationManager = ({ currentConfig, onConfigChange }) => {
    const { getAccessTokenSilently } = useAuth0();
    const [configurations, setConfigurations] = useState([]);
    const [selectedConfig, setSelectedConfig] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [saveNameVisible, setSaveNameVisible] = useState(false);
    const [newConfigName, setNewConfigName] = useState('');
    const [isDefault, setIsDefault] = useState(false);
    const [importModalVisible, setImportModalVisible] = useState(false);
    const [confirmDeleteVisible, setConfirmDeleteVisible] = useState(false);
    const { selectedTenant } = useSharedData();

    // Helper function to fetch the token
    const getToken = async () => {
        try {
            return await getAccessTokenSilently();
        } catch (error) {
            console.error('Error getting access token:', error);
            message.error('Failed to authenticate. Please log in again.');
            throw error;
        }
    };

    // Fetch configurations on component mount
    useEffect(() => {
        fetchConfigurations();
    }, []);

    // Check for unsaved changes when currentConfig changes
    useEffect(() => {
        if (selectedConfig && currentConfig) {
            // Only compare the parts of the configuration we care about
            // and normalize the comparison to avoid false positives
            const normalizeConfig = (config) => {
                const normalized = { ...config };

                // Remove any empty arrays or objects
                Object.keys(normalized).forEach(key => {
                    if (Array.isArray(normalized[key]) && normalized[key].length === 0) {
                        delete normalized[key];
                    }
                    if (
                        typeof normalized[key] === 'object' &&
                        normalized[key] !== null &&
                        Object.keys(normalized[key]).length === 0
                    ) {
                        delete normalized[key];
                    }
                });

                // Special handling for filters, visibleFilterIds, activeFilters
                // This avoids false positives from different object shapes with same content
                if (normalized.visibleFilterIds) {
                    normalized.visibleFilterIds = [...normalized.visibleFilterIds].sort();
                }

                if (normalized.activeFilters) {
                    // Sort array values to ensure consistent comparison
                    const sortedActiveFilters = {};
                    Object.keys(normalized.activeFilters).forEach(key => {
                        if (Array.isArray(normalized.activeFilters[key])) {
                            const values = [...normalized.activeFilters[key]];
                            if (values.length > 0) {
                                sortedActiveFilters[key] = values.sort();
                            }
                        } else {
                            sortedActiveFilters[key] = normalized.activeFilters[key];
                        }
                    });

                    // Only include non-empty filter values
                    if (Object.keys(sortedActiveFilters).length > 0) {
                        normalized.activeFilters = sortedActiveFilters;
                    } else {
                        delete normalized.activeFilters;
                    }
                }

                return normalized;
            };

            // Normalize both configs for comparison
            const normalizedCurrent = normalizeConfig(currentConfig);
            const normalizedSelected = normalizeConfig(selectedConfig.config);

            // Compare the stringified versions
            const isEqual = JSON.stringify(normalizedCurrent) === JSON.stringify(normalizedSelected);

            // Only update if actually changed to avoid re-renders
            if (hasUnsavedChanges !== !isEqual) {
                setHasUnsavedChanges(!isEqual);
            }
        }
    }, [currentConfig, selectedConfig, hasUnsavedChanges]);

    const fetchConfigurations = async () => {
        setIsLoading(true);
        try {
            const token = await getToken();
            const response = await axios.get('/api/analytics-configurations', {
                headers: { Authorization: `Bearer ${token}`, 'x-tenant': selectedTenant },
            });
            const configs = response.data;
            setConfigurations(configs);

            // Select default config
            const defaultConfig = configs.find((config) => config.isDefault);

            if (defaultConfig) {
                setSelectedConfig(defaultConfig);
                onConfigChange(defaultConfig.config);
            } else if (configs.length > 0) {
                setSelectedConfig(configs[0]);
                onConfigChange(configs[0].config);
            } else {
                setSelectedConfig({ name: 'Default System View', config: analyticsDefaults });
                onConfigChange(analyticsDefaults);
            }
        } catch (error) {
            console.error('Error fetching configurations:', error);
            message.error('Failed to load configurations');
            setSelectedConfig({ name: 'Default System View', config: analyticsDefaults });
            onConfigChange(analyticsDefaults);
        } finally {
            setIsLoading(false);
        }
    };

    // Save current configuration changes to the selected config
    const handleSaveChanges = async () => {
        if (!selectedConfig.id) {
            // If no saved config is selected, show the save as new dialog
            setSaveNameVisible(true);
            return;
        }

        setIsSaving(true);
        try {
            const token = await getToken();
            const configData = {
                name: selectedConfig.name,
                config: currentConfig,
                isDefault: selectedConfig.isDefault,
            };

            const response = await axios.put(`/api/analytics-configurations/${selectedConfig.id}`, configData, {
                headers: { Authorization: `Bearer ${token}`, 'x-tenant': selectedTenant },
            });

            // Update configurations list
            setConfigurations(configurations.map((c) =>
                c.id === selectedConfig.id ? response.data : c
            ));

            // Update selected config
            setSelectedConfig(response.data);
            setHasUnsavedChanges(false);
            message.success('Configuration saved successfully');
        } catch (error) {
            console.error('Error saving configuration:', error);
            message.error('Failed to save configuration');
        } finally {
            setIsSaving(false);
        }
    };

    // Save as a new configuration
    const handleSaveAsNew = async () => {
        if (!newConfigName) {
            message.error('Configuration name is required');
            return;
        }

        setIsSaving(true);
        try {
            const token = await getToken();
            const configData = {
                name: newConfigName,
                config: currentConfig,
                isDefault: isDefault,
            };

            const response = await axios.post('/api/analytics-configurations', configData, {
                headers: { Authorization: `Bearer ${token}`, 'x-tenant': selectedTenant },
            });

            // If setting as default, update all other configs
            if (isDefault) {
                const updatedConfigs = configurations.map(c => ({
                    ...c,
                    isDefault: false
                }));
                setConfigurations([...updatedConfigs, response.data]);
            } else {
                setConfigurations([...configurations, response.data]);
            }

            setSelectedConfig(response.data);
            setHasUnsavedChanges(false);
            message.success('Configuration saved successfully');
            setSaveNameVisible(false);
            setNewConfigName('');
            setIsDefault(false);
        } catch (error) {
            console.error('Error saving configuration:', error);
            message.error('Failed to save configuration');
        } finally {
            setIsSaving(false);
        }
    };

    // Set a configuration as the default
    const handleSetDefault = async (config) => {
        try {
            const token = await getToken();

            // First, update the selected config to be the default
            const configData = {
                ...config,
                isDefault: true
            };

            const response = await axios.put(`/api/analytics-configurations/${config.id}`, configData, {
                headers: { Authorization: `Bearer ${token}`, 'x-tenant': selectedTenant },
            });

            // Then, update all other configs to not be default
            // This is an optimization to avoid having to refetch all configs
            const updatedConfigs = configurations.map((c) => ({
                ...c,
                isDefault: c.id === config.id
            }));

            setConfigurations(updatedConfigs);
            message.success(`"${config.name}" set as default configuration`);
        } catch (error) {
            console.error('Error setting default configuration:', error);
            message.error('Failed to set default configuration');
        }
    };

    // Delete a configuration
    const handleDeleteConfig = async () => {
        if (!selectedConfig?.id) {
            message.error('No configuration selected to delete');
            return;
        }

        setIsLoading(true);
        try {
            const token = await getToken();
            await axios.delete(`/api/analytics-configurations/${selectedConfig.id}`, {
                headers: { Authorization: `Bearer ${token}`, 'x-tenant': selectedTenant },
            });

            const updatedConfigs = configurations.filter((c) => c.id !== selectedConfig.id);
            setConfigurations(updatedConfigs);

            // Select first available config or default to empty
            if (updatedConfigs.length > 0) {
                const defaultConfig = updatedConfigs.find(c => c.isDefault) || updatedConfigs[0];
                setSelectedConfig(defaultConfig);
                onConfigChange(defaultConfig.config);
            } else {
                setSelectedConfig({ name: 'Default System View', config: analyticsDefaults });
                onConfigChange(analyticsDefaults);
            }

            setHasUnsavedChanges(false);
            message.success('Configuration deleted successfully');
            setConfirmDeleteVisible(false);
        } catch (error) {
            console.error('Error deleting configuration:', error);
            message.error('Failed to delete configuration');
        } finally {
            setIsLoading(false);
        }
    };

    // Export configuration to JSON
    const handleExportConfig = () => {
        // Export the CURRENT config (not the saved one)
        // This ensures all changes are included in the export
        if (!currentConfig) {
            message.error('No configuration to export');
            return;
        }

        const configToExport = {
            name: selectedConfig?.name || 'Untitled Configuration',
            config: currentConfig
        };

        const blob = new Blob([JSON.stringify(configToExport, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${configToExport.name.replace(/\s+/g, '_')}.json`;
        a.click();
        URL.revokeObjectURL(url);
        message.success('Configuration exported successfully');
    };

    // Import configuration from JSON
    const handleImport = (event) => {
        const file = event.target.files[0];
        if (!file) return;

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const imported = JSON.parse(e.target.result);
                // Check if it's a valid configuration
                if (imported.config) {
                    onConfigChange(imported.config);
                    setNewConfigName(imported.name || 'Imported Configuration');
                    setSaveNameVisible(true);
                    setImportModalVisible(false);
                    message.success('Configuration imported successfully. Save to persist.');
                } else {
                    message.error('Invalid configuration format');
                }
            } catch (error) {
                console.error('Error importing configuration:', error);
                message.error('Invalid configuration file');
            }
        };
        reader.readAsText(file);
    };

    // Handle selecting a configuration
    const handleSelectConfig = (config) => {
        // If there are unsaved changes, confirm before switching
        if (hasUnsavedChanges) {
            Modal.confirm({
                title: 'Unsaved Changes',
                content: 'You have unsaved changes to the current configuration. Do you want to discard them?',
                okText: 'Discard Changes',
                okType: 'danger',
                cancelText: 'Keep Editing',
                onOk: () => {
                    setSelectedConfig(config);
                    onConfigChange(config.config);
                    setHasUnsavedChanges(false);
                }
            });
        } else {
            setSelectedConfig(config);
            onConfigChange(config.config);
        }
    };

    // Create options menu for configurations
    const configMenu = (
        <Menu>
            {/* Default system configuration */}
            <Menu.Item
                key="default-system"
                onClick={() => {
                    if (hasUnsavedChanges) {
                        Modal.confirm({
                            title: 'Unsaved Changes',
                            content: 'You have unsaved changes to the current configuration. Do you want to discard them?',
                            okText: 'Discard Changes',
                            okType: 'danger',
                            cancelText: 'Keep Editing',
                            onOk: () => {
                                setSelectedConfig({ name: 'Default System View', config: analyticsDefaults });
                                onConfigChange(analyticsDefaults);
                                setHasUnsavedChanges(false);
                            }
                        });
                    } else {
                        setSelectedConfig({ name: 'Default System View', config: analyticsDefaults });
                        onConfigChange(analyticsDefaults);
                    }
                }}
            >
                <div style={{
                    maxWidth: '200px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}>
          <span style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
          }}>
            Default System View
          </span>
                    {!selectedConfig?.id && <span style={{ color: '#1890ff', marginLeft: 8 }}>✓</span>}
                </div>
            </Menu.Item>

            {configurations.length > 0 && <Menu.Divider />}

            {configurations.map((config) => (
                <Menu.Item
                    key={config.id}
                    onClick={() => handleSelectConfig(config)}
                    icon={config.isDefault ? <StarFilled style={{ color: '#faad14' }} /> : <span style={{ width: 14, display: 'inline-block' }} />}
                >
                    <div style={{
                        maxWidth: '200px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
            <span style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
            }}>
              {config.name}
            </span>
                        {selectedConfig?.id === config.id && <span style={{ color: '#1890ff', marginLeft: 8 }}>✓</span>}
                    </div>
                </Menu.Item>
            ))}

            <Menu.Divider />

            <Menu.Item key="new" icon={<PlusOutlined />} onClick={() => setSaveNameVisible(true)}>
                Save As New
            </Menu.Item>

            <Menu.Item key="import" icon={<ImportOutlined />} onClick={() => setImportModalVisible(true)}>
                Import Configuration
            </Menu.Item>
        </Menu>
    );

    // Actions menu (for the currently selected configuration)
    const actionsMenu = (
        <Menu>
            <Menu.Item
                key="save"
                icon={<SaveOutlined />}
                disabled={!hasUnsavedChanges || isSaving || !selectedConfig?.id}
                onClick={handleSaveChanges}
            >
                Save Changes
            </Menu.Item>

            <Menu.Item
                key="saveAs"
                icon={<FileOutlined />}
                onClick={() => setSaveNameVisible(true)}
            >
                Save As New
            </Menu.Item>

            {selectedConfig?.id && (
                <>
                    <Menu.Divider />

                    <Menu.Item
                        key="setDefault"
                        icon={<StarOutlined />}
                        disabled={selectedConfig.isDefault}
                        onClick={() => handleSetDefault(selectedConfig)}
                    >
                        Set as Default
                    </Menu.Item>

                    <Menu.Item
                        key="export"
                        icon={<ExportOutlined />}
                        onClick={handleExportConfig}
                    >
                        Export
                    </Menu.Item>

                    <Menu.Item
                        key="delete"
                        icon={<DeleteOutlined />}
                        danger
                        onClick={() => setConfirmDeleteVisible(true)}
                    >
                        Delete
                    </Menu.Item>
                </>
            )}
        </Menu>
    );
    //console.log("Rendering ImprovedConfigurationManager");
    return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
            {/* Main configuration selector */}
            <Dropdown
                overlay={configMenu}
                trigger={['click']}
                disabled={isLoading}
            >
                <Button style={{
                    display: 'flex',
                    alignItems: 'center',
                    minWidth: '180px',
                    justifyContent: 'space-between'
                }}>
                    <Text
                        ellipsis={{ tooltip: selectedConfig?.name || 'Default System View' }}
                        style={{ maxWidth: 150 }}
                    >
                        {isLoading ? 'Loading...' : (selectedConfig?.name || 'Default System View')}
                    </Text>
                    {isLoading ? <LoadingOutlined /> : <DownOutlined />}
                </Button>
            </Dropdown>

            {/* Indicator for unsaved changes */}
            {hasUnsavedChanges && (
                <Tooltip title="You have unsaved changes">
                    <Text type="warning" style={{ margin: '0 8px' }}>
                        ●
                    </Text>
                </Tooltip>
            )}

            {/* Action button - save, export, etc. */}
            <Dropdown overlay={actionsMenu} trigger={['click']}>
                <Button
                    icon={<SettingOutlined />}
                    type={hasUnsavedChanges ? "primary" : "default"}
                />
            </Dropdown>

            {/* Quick save button - appears when there are unsaved changes */}
            {hasUnsavedChanges && selectedConfig?.id && (
                <Tooltip title="Save changes">
                    <Button
                        icon={<SaveOutlined />}
                        type="primary"
                        style={{ marginLeft: 8 }}
                        onClick={handleSaveChanges}
                        loading={isSaving}
                    />
                </Tooltip>
            )}

            {/* Save As Modal */}
            <Modal
                title="Save Configuration"
                visible={saveNameVisible}
                onCancel={() => setSaveNameVisible(false)}
                footer={[
                    <Button key="cancel" onClick={() => setSaveNameVisible(false)}>
                        Cancel
                    </Button>,
                    <Button
                        key="save"
                        type="primary"
                        onClick={handleSaveAsNew}
                        loading={isSaving}
                        disabled={!newConfigName}
                    >
                        Save
                    </Button>
                ]}
            >
                <Input
                    placeholder="Configuration Name"
                    value={newConfigName}
                    onChange={(e) => setNewConfigName(e.target.value)}
                    style={{ marginBottom: 16 }}
                />
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Switch
                        checked={isDefault}
                        onChange={setIsDefault}
                    />
                    <span style={{ marginLeft: 10 }}>Set as Default</span>
                </div>
            </Modal>

            {/* Import Modal */}
            <Modal
                title="Import Configuration"
                visible={importModalVisible}
                onCancel={() => setImportModalVisible(false)}
                footer={[
                    <Button key="cancel" onClick={() => setImportModalVisible(false)}>
                        Cancel
                    </Button>
                ]}
            >
                <p>Select a configuration JSON file to import:</p>
                <input
                    type="file"
                    accept=".json"
                    onChange={handleImport}
                    style={{ marginTop: 10 }}
                />
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal
                title="Delete Configuration"
                visible={confirmDeleteVisible}
                onCancel={() => setConfirmDeleteVisible(false)}
                footer={[
                    <Button key="cancel" onClick={() => setConfirmDeleteVisible(false)}>
                        Cancel
                    </Button>,
                    <Button
                        key="delete"
                        danger
                        type="primary"
                        onClick={handleDeleteConfig}
                        loading={isLoading}
                    >
                        Delete
                    </Button>
                ]}
            >
                <p>Are you sure you want to delete the configuration "{selectedConfig?.name}"?</p>
                <p>This action cannot be undone.</p>
            </Modal>
        </div>
    );
};

export default ImprovedConfigurationManager;
